import { useEffect, useState } from 'react';
import { primaryButtonStyle } from '@kilat/ui';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { tenantsRepo } from '../lib/repositories/tenants-repo.js';
import { outletDocRepoFor } from '../lib/repositories/outlet-repos.js';
import { logger } from '../lib/logger.js';
import { QrPrintCard } from '../components/qr-print-card.js';
import { PAGE_CONTENT_MAX } from '../lib/layout.js';
import { customerAppUrl, type TenantProfile } from '@kilat/shared';

/**
 * /qr-print — full-page printable QR card for the active outlet.
 *
 * Operator opens this from PostOnboardingSummary or /queue, hits Cmd/Ctrl+P,
 * gets a clean one-card-per-page printout to stick at the counter or laminate
 * for the entrance door.
 *
 * Encodes:
 *   https://{marketingDomain}/{slug}/o/{outletId}
 * which the customer-pwa /o/:outletId route consumes to set the active outlet.
 *
 * This URL is PHYSICAL. An operator prints it, laminates it, and puts it on a
 * wall — so unlike every other link in the product, a wrong one cannot be
 * hot-fixed. It built the pre-ADR-0039 subdomain shape until 2026-08-21, which
 * meant every printed card pointed at a host with no DNS record.
 */
export function QrPrintPage() {
  const { claims } = useAuth();
  const { t } = useI18n();
  const brand = usePlatformBrand();
  const { outletId, outlets, loading: outletLoading } = useActiveOutlet();

  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [outletName, setOutletName] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const tenant = await tenantsRepo.get(tenantId);
        if (cancelled) return;
        setProfile(tenant?.profile ?? null);
        setProfileLoaded(true);
      } catch (e) {
        logger.error('qr-print: tenant load failed', { tenantId, error: String(e) });
        if (!cancelled) setProfileLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Outlet name: prefer the live outlet doc's profile.name; the switcher
  // cache is fine, but the doc has the canonical address-line too if we
  // ever want it on the card.
  useEffect(() => {
    if (!tenantId || !outletId) return;
    const fromList = outlets.find((o) => o.id === outletId);
    if (fromList) {
      setOutletName(fromList.name);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const doc = await outletDocRepoFor(tenantId).get(outletId);
        if (cancelled) return;
        const outletProfile = doc?.profile as { name?: string } | undefined;
        setOutletName(outletProfile?.name ?? outletId);
      } catch {
        if (!cancelled) setOutletName(outletId);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, outletId, outlets]);

  if (!tenantId) return <Screen body={t('dashboard.qrPrint.signInBody')} />;
  if (!profileLoaded || outletLoading) return <Screen body={t('common.loading')} />;
  if (!profile) return <Screen body={t('dashboard.qrPrint.tenantNotFound')} />;
  if (!outletId) return <Screen body={t('dashboard.qrPrint.pickOutlet')} />;

  const url = `${customerAppUrl(brand.domains, profile.slug)}o/${outletId}`;

  return (
    <main
      className="qr-print-page min-h-dvh flex flex-col items-center px-6 py-12"
      style={{ background: 'var(--color-surface)', color: 'var(--color-on-surface)' }}
    >
      {/* Controls beside the printable preview once both fit side by side, so the
          card is seen at its print size next to the button that prints it;
          stacked above it on a phone. Print collapses this back to the single
          centred card (see the @media print rule below). */}
      <div
        className="qr-print-layout"
        style={{
          width: '100%',
          maxWidth: PAGE_CONTENT_MAX,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 26rem), 1fr))',
          gap: 'var(--space-8)',
          alignItems: 'start',
        }}
      >
        <header className="no-print" style={{ width: '100%', minWidth: 0 }}>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              letterSpacing: 4,
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            {brand.name} · {t('dashboard.qrPrint.eyebrowQr')}
          </p>
          <h1
            style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 'var(--space-2) 0 0 0' }}
          >
            {t('dashboard.qrPrint.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              marginTop: 'var(--space-2)',
            }}
          >
            {t('dashboard.qrPrint.subtitle')}
          </p>
          <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-2)' }}>
            <button type="button" onClick={() => window.print()} style={primaryButtonStyle}>
              {t('dashboard.qrPrint.printButton')}
            </button>
            <a href="/queue" style={secondaryButtonStyle}>
              {t('dashboard.states.backToQueue')}
            </a>
          </div>
        </header>

        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <QrPrintCard
            url={url}
            brandName={profile.businessName}
            outletName={outletName ?? outletId}
          />
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .qr-print-page { padding: 0 !important; min-height: auto !important; }
          .qr-print-layout { display: flex !important; flex-direction: column; align-items: center; }
        }
      `}</style>
    </main>
  );
}

function Screen({ body }: { readonly body: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </main>
  );
}

const secondaryButtonStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border-strong)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  textDecoration: 'none',
  fontWeight: 500,
};
